// Printable test review (route OUTSIDE AdminLayout; opened from the student's tests list).
// C11.3: a Version 2 test / revision (any status) comes from /api/v2/admin/tests/:id/review;
// an old-app test still comes from the old /api/tests/:id/admin-result (unchanged), and its
// questions get the PYQ tag, the explanation and the notes references from
// /api/v2/admin/print-questions. Print with the browser ("Save as PDF").
import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAdminResult } from '../../api';
import { v2PrintQuestions, v2TestReview } from '../../api/v2admin';
import { PRINT_CSS, PrintQuestion, StatGrid, fmtDate } from './v2/PrintParts';

const pct = (v) => `${Number(v || 0).toFixed(1)}%`;

export default function AdminTestReview() {
  const { testId } = useParams();

  const v2 = useQuery({
    queryKey: ['v2TestReview', testId],
    queryFn: () => v2TestReview(testId),
    retry: false,
  });
  const isOld = v2.isError && v2.error?.response?.status === 404;

  const old = useQuery({
    queryKey: ['adminResult', testId],
    queryFn: () => getAdminResult(testId).then(r => r.data),
    enabled: isOld,
  });
  const oldIds = (old.data?.questions || []).map(q => q.question_id);
  const extras = useQuery({
    queryKey: ['v2PrintExtras', testId, oldIds.join(',')],
    queryFn: () => v2PrintQuestions(oldIds.join(',')),
    enabled: isOld && oldIds.length > 0 && oldIds.length <= 300,
    retry: false,
  });

  const loading = v2.isLoading || (isOld && (old.isLoading || (oldIds.length > 0 && oldIds.length <= 300 && extras.isLoading)));
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}><div className="spinner" /></div>;

  let data = null;
  let rawExpl = {};
  if (v2.data) {
    data = v2.data;
  } else if (old.data) {
    const by = {};
    (extras.data?.questions || []).forEach(x => { by[x.question_id] = x; });
    data = {
      ...old.data,
      kind: 'Old app',
      questions: old.data.questions.map((q, i) => {
        const x = by[q.question_id];
        if (!x) { rawExpl[q.question_id] = q.explanation; }
        return {
          ...q, number: i + 1,
          pyq: x ? x.pyq : null,
          notes_refs: x ? x.notes_refs : [],
          explanation: x ? x.explanation : null,
          explanation_image_url: x ? x.explanation_image_url : q.explanation_image_url,
        };
      }),
    };
  }
  if (!data) {
    return (
      <div style={{ padding: 40, fontFamily: 'DM Sans, sans-serif' }}>
        Could not load this test. {v2.error?.response?.data?.detail || old.error?.response?.data?.detail || ''}
      </div>
    );
  }

  const { student_name, test_title, kind, status, started_at, submitted_at, score, max_score,
          accuracy_pct, attempt_pct, correct, incorrect, attempted, total_questions,
          questions, one_liners } = data;
  const open = status && status !== 'completed';

  return (
    <div className="pq-page" style={{ maxWidth: 900, margin: '0 auto', padding: '32px 20px', fontFamily: 'DM Sans, sans-serif' }}>
      <style>{PRINT_CSS}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 24, fontFamily: 'Sora', marginBottom: 4 }}>{test_title || 'Test'}</h1>
          <p style={{ color: '#64748B', fontSize: 14 }}>
            {kind && <><strong data-testid="pq-kind">{kind}</strong> &nbsp;·&nbsp; </>}
            Student: <strong>{student_name}</strong> &nbsp;·&nbsp;
            {open ? `Started ${fmtDate(started_at)} · still open` : fmtDate(submitted_at || started_at)}
          </p>
        </div>
        <button className="btn btn-primary no-print" onClick={() => window.print()}>Print / Save PDF</button>
      </div>

      <StatGrid items={[
        { label: 'Score', value: score != null ? `${score}/${max_score}` : '—', color: '#4F46E5' },
        { label: 'Answered', value: `${attempted}/${total_questions}`, color: '#06B6D4' },
        { label: 'Attempt %', value: pct(attempt_pct), color: '#06B6D4' },
        { label: 'Accuracy %', value: pct(accuracy_pct), color: '#10B981' },
        { label: 'Correct', value: correct, color: '#10B981' },
        { label: 'Wrong', value: incorrect, color: '#EF4444' },
      ]} />
      {one_liners && (
        <p data-testid="pq-one-liners" style={{ fontSize: 14, color: '#475569', marginTop: -12, marginBottom: 20 }}>
          One-liners in this revision: {one_liners.done} answered · {one_liners.right} right · {one_liners.wrong} wrong (only the questions are printed below)
        </p>
      )}

      {questions.length === 0 ? (
        <p style={{ color: '#64748B' }}>No questions in this {kind === 'Revision' ? 'revision' : 'test'}.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {questions.map((q, i) => (
            <PrintQuestion key={`${q.question_id}-${i}`} q={q} index={q.number || i + 1} withStudent
              explanationText={rawExpl[q.question_id]} />
          ))}
        </div>
      )}
    </div>
  );
}
